import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@uconnect/assets/lib/utils";

const defaultTabVariant = "buttonPanel";

const buttonPanelListClasses =
	"bg-border text-navy text-base font-semibold inline-flex w-fit items-center justify-center rounded-lg p-[3px]";

const tabsListVariants = cva( "", {
	variants: {
		variant: {
			buttonPanel: buttonPanelListClasses,
			buttonPanelSmall: buttonPanelListClasses,
			underline: "flex! border-b! border-border w-full!",
		},
	},
	defaultVariants: {
		variant: defaultTabVariant,
	},
} );

const buttonPanelTriggerClasses = "data-[state=active]:bg-background gap-1.5 rounded-md border border-transparent";

const tabsTriggerVariants = cva(
	"inline-flex! flex-1 items-center justify-center cursor-pointer focus-visible:ring-ring/50 focus-visible:outline-ring focus-visible:ring-[2px] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 whitespace-nowrap transition-colors",
	{
		variants: {
			variant: {
				buttonPanel: `${ buttonPanelTriggerClasses } px-4 py-2`,
				buttonPanelSmall: `${ buttonPanelTriggerClasses } px-2 py-1 text-xs! leading-[1.2]`,
				underline:
					"p-2.5 border-b-3! border-transparent data-[state=active]:border-b-primary text-default! data-[state=active]:text-primary! hover:bg-primary/10 font-semibold! truncate focus-visible:shadow-none! focus-visible:ring-0! focus-visible:-outline-offset-3! focus-visible:outline-3!",
			},
		},
		defaultVariants: {
			variant: defaultTabVariant,
		},
	}
);

type TabsVariant = NonNullable<VariantProps<typeof tabsListVariants>["variant"]>;

const TabsContext = React.createContext<{ variant: TabsVariant }>( {
	variant: defaultTabVariant,
} );

function Tabs( {
	className,
	variant = defaultTabVariant,
	...props
}: React.ComponentProps<typeof TabsPrimitive.Root> & {
	variant?: TabsVariant;
} ) {
	return (
		<TabsContext.Provider value={ { variant } }>
			<TabsPrimitive.Root
				data-slot="tabs"
				className={ cn( "flex flex-col gap-2", className ) }
				{ ...props }
			/>
		</TabsContext.Provider>
	);
}

function TabsList( {
	className,
	...props
}: React.ComponentProps<typeof TabsPrimitive.List> ) {
	const { variant } = React.useContext( TabsContext );
	return (
		<TabsPrimitive.List
			data-slot="tabs-list"
			className={ cn( tabsListVariants( { variant } ), className ) }
			{ ...props }
		/>
	);
}

function TabsTrigger( {
	className,
	...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> ) {
	const { variant } = React.useContext( TabsContext );
	return (
		<TabsPrimitive.Trigger
			data-slot="tabs-trigger"
			className={ cn( tabsTriggerVariants( { variant } ), className ) }
			{ ...props }
		/>
	);
}

function TabsContent( {
	className,
	...props
}: React.ComponentProps<typeof TabsPrimitive.Content> ) {
	return (
		<TabsPrimitive.Content
			data-slot="tabs-content"
			className={ cn( "flex-1 outline-none", className ) }
			{ ...props }
		/>
	);
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
export type { TabsVariant };
